import { Badge, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { keyframes } from '@mui/system'
import React, { useState } from 'react'
import useRepeatableAnimation from '../../../../hooks/useRepeatableAnimation'

type Props = {
    onClick: () => void
    disabled?: boolean
    children?: React.ReactNode
    title?: string
    icon?: React.ReactNode
    badge?: number
    badgeColor?: string
    active?: boolean
    showTooltip?: boolean
    description?: string
    animated?: boolean
    removeBorder?: boolean
}

const pulseCycle = keyframes`
  0% { transform: scale(1); }
  6.67% { transform: scale(1.25); }
  13.33% { transform: scale(0.9); }
  20% { transform: scale(1.25); }
  26.67% { transform: scale(0.9); }
  33.33% { transform: scale(1.25); }
  40% { transform: scale(1); }
  100% { transform: scale(1); }
`

const CircularButton = ({
    onClick,
    disabled,
    title,
    icon,
    badge,
    active,
    showTooltip,
    animated = false,
    description,
    removeBorder = false,
}: Props) => {
    const [tooltipOpen, setTooltipOpen] = useState<boolean>(false)
    const { isAnimating } = useRepeatableAnimation(animated && !disabled)
    const effectiveTooltipOpen = description && showTooltip === true ? true : tooltipOpen

    return (
        <Tooltip
            title={description}
            placement="top"
            arrow
            open={effectiveTooltipOpen}
            onOpen={() => {
                if (!showTooltip) {
                    setTooltipOpen(true)
                }
            }}
            onClose={() => {
                if (!showTooltip) {
                    setTooltipOpen(false)
                }
            }}
        >
            <Stack gap={0.5} alignItems="center" justifyContent="center">
                <Badge
                    sx={(theme) => ({
                        '& .MuiBadge-badge': {
                            top: '.3rem',
                            right: '.3rem',
                            minWidth: '1.25rem',
                            height: '1.25rem',
                            padding: '0 .375rem',
                            lineHeight: '1rem',
                            zIndex: showTooltip ? theme.zIndex.drawer + 100 : 'auto',
                            pointerEvents: 'none',
                            color: 'white',
                            backgroundColor: '#D80031',
                            fontWeight: 'bold',
                        },
                        animation: isAnimating ? `${pulseCycle} 5s linear 0s 3` : 'none',
                    })}
                    color="error"
                    badgeContent={badge}
                    showZero
                    max={99}
                >
                    <IconButton
                        onClick={onClick}
                        color="primary"
                        size="large"
                        disabled={disabled}
                        sx={(theme) => ({
                            zIndex: showTooltip ? theme.zIndex.drawer + 100 : 'auto',
                            height: '40px',
                            width: '40px',
                            borderRadius: '100%',
                            border: removeBorder
                                ? 'none'
                                : `0.125rem solid ${active ? theme.palette.primary.main : theme.palette.text.primary}`,
                            padding: '0px !important',
                            svg: {
                                color: active ? theme.palette.primary.main : theme.palette.text.primary,
                                path: {
                                    // fill: theme.palette.text.primary,
                                },
                            },
                        })}
                    >
                        {icon}
                    </IconButton>
                </Badge>
                {title && (
                    <Typography
                        variant="caption"
                        sx={(theme) => ({
                            fontSize: '10px',
                            lineHeight: 1.2,
                            zIndex: showTooltip ? theme.zIndex.drawer + 100 : 'auto',
                            fontWeight: 700,
                            color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
                        })}
                    >
                        {title}
                    </Typography>
                )}
            </Stack>
        </Tooltip>
    )
}

export default CircularButton
